<template>
  <div class="purpose-screen">
    <h1>What's your primary purpose for using AudioScribe?</h1>

    <form
      @submit.prevent="handleSubmit"
      class="login-form"
      :class="{ 'error-active': hasError }"
    >
      <div class="purpose-options">
        <div
          v-for="option in purposeOptions"
          :key="option.value"
          class="onboarding-option-card"
          :class="{ selected: preferences.primaryPurpose === option.value }"
          @click.prevent="selectPurpose(option.value)"
        >
          <div class="onboarding-radio-container">
            <span
              class="onboarding-radio-circle"
              :class="{ checked: preferences.primaryPurpose === option.value }"
            ></span>
            <span class="onboarding-radio-text">
              <span class="option-title">{{ option.title }}</span>
              <span v-if="option.description" class="option-description">{{
                option.description
              }}</span>
            </span>
          </div>
        </div>
      </div>

      <div v-if="hasError" class="onboarding-form-error">
        <span class="error-icon icon-exclamation-mark"></span>
        <span class="error-txt">{{ validationErrors.primaryPurpose }}</span>
      </div>

      <div class="btn-holder">
        <input
          type="submit"
          class="btn"
          value="Next"
          :disabled="isSubmitting"
        />
      </div>
    </form>
  </div>
</template>

<script>
import { useOnboardingStore } from "@/stores/onboarding-store";
import { mapStores } from "pinia";
import { analytics } from "@/services/analytics.service";

export default {
  name: "PrimaryPurposeScreen",
  computed: {
    ...mapStores(useOnboardingStore),
    preferences() {
      return this.onboardingStore.preferences;
    },
    validationErrors() {
      return this.onboardingStore.validationErrors;
    },
    isSubmitting() {
      return this.onboardingStore.isSubmitting;
    },
    hasError() {
      return !!this.validationErrors.primaryPurpose;
    },
  },
  mounted() {
    // Track step 1 viewed
    analytics.onboardingStep1Viewed();
  },
  data() {
    return {
      purposeOptions: [
        {
          value: "PROFESSIONAL",
          title: "Professional Use",
          description: "business meetings, interviews, legal",
        },
        {
          value: "ACADEMIC",
          title: "Academic",
          description: "research, lectures, studying",
        },
        {
          value: "MEDIA",
          title: "Media Content",
          description: "YouTube, Instagram, podcasts, social media",
        },
        {
          value: "PERSONAL",
          title: "Personal Use",
          description: "messages, notes, personal recordings",
        },
        {
          value: "GAMING",
          title: "Gaming",
          description: "RPGs, game sessions, livestreams",
        },
        {
          value: "RELIGIOUS",
          title: "Religious Content",
          description: "sermons, community content",
        },
        {
          value: "OTHER",
          title: "Other",
          description: "",
        },
      ],
    };
  },
  methods: {
    selectPurpose(value) {
      this.preferences.primaryPurpose = value;

      // Track purpose selected
      analytics.onboardingPurposeSelected({ purpose: value });

      // Clear validation errors
      this.onboardingStore.validationErrors = {};
    },
    handleSubmit() {
      if (this.onboardingStore.validateCurrentStep()) {
        // Track step 1 completed
        analytics.onboardingStep1Completed({
          purpose: this.preferences.primaryPurpose,
        });

        this.$emit("nextStep");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:color";

.purpose-screen {
  @include onboarding-screen-title;
}

.purpose-options {
  @include onboarding-options-list;
}

// First screen has a centered single button instead of Back/Next
.btn-holder {
  margin-top: 30px;

  .btn {
    background-color: $primary;
    color: $white;
    border: none;
    border-radius: 25px;
    padding: 12px 24px;
    font-weight: 500;
    font-size: 16px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    width: 100%;
    max-width: 200px;
    margin: 0 auto;
    display: block;

    &:hover {
      background-color: color.adjust($primary, $lightness: -10%);
    }

    &:disabled {
      background-color: $gray700;
      cursor: not-allowed;
    }
  }
}
</style>
